import {useApolloClient} from '@apollo/client';
import {none} from '@danwang/option';
import {useUpdateRawTransactionsMutation} from '@transactions/graphql';
import * as React from 'react';
import AccountSelect from 'src/AccountSelect';
import evictAccountRawTransactions from 'src/apolloCache/evictAccountRawTransactions';
import {Button} from 'src/Button';
import InputGroup from 'src/InputGroup';
import {Modal} from 'src/Modal';
import type {Option} from '@danwang/option';
import type {
  AccountListItemFragment,
  BasicRawTransactionFragment,
} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type UpdateRawTransactionsModalUserProps = {
  rawTransactions: BasicRawTransactionFragment[];
  onUpdate: () => void;
};

export const UpdateRawTransactionsModal = (
  props: UpdateRawTransactionsModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const {rawTransactions, onUpdate, onRequestClose} = props;
  const client = useApolloClient();
  const [selectedAccount, setSelectedAccount] =
    React.useState<Option<AccountListItemFragment>>(none);

  const [updateRawTransactions, {loading}] = useUpdateRawTransactionsMutation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    selectedAccount.forEach((account) => {
      const movedIds = rawTransactions.map(({_id}) => _id);
      updateRawTransactions({
        variables: {
          ids: movedIds,
          account: account._id,
        },
      }).then(() => {
        // Moving rewrites each row's accountId, so any cached AllRawTransactions
        // page for the source account(s) or the destination — across every
        // filter/pagination variant — is now stale. The list query is
        // cache-first, so evict those pages to force a refetch on next view.
        // Centralized here (not in a caller) so every entry point stays correct.
        const affectedAccountIds = new Set(
          rawTransactions.map((rt) => rt.account._id),
        );
        affectedAccountIds.add(account._id);
        affectedAccountIds.forEach((accountId) =>
          evictAccountRawTransactions(client.cache, accountId),
        );
        onRequestClose();
        onUpdate();
      });
    });
  };

  const footer = (
    <>
      <Button variant="outline" disabled={loading} onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={loading || selectedAccount.isEmpty}
        type="submit"
        form={formId}
      >
        Update
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Move transactions"
      footer={footer}
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <InputGroup label="Account">
          <AccountSelect
            autoFocus
            selectedAccount={selectedAccount}
            onSelectAccount={setSelectedAccount}
            // Raws can only move to an account in their own currency. A
            // mixed-currency selection has no valid destination; leave the
            // list unfiltered and let the server's per-row check explain.
            currency={
              new Set(rawTransactions.map((rt) => rt.currency)).size === 1
                ? rawTransactions[0]?.currency
                : undefined
            }
          />
        </InputGroup>
      </form>
    </Modal>
  );
};
